import React, { useState } from "react";
import { Dropdown } from "react-bootstrap";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { formatReportDate } from "../../utils/dateFormat";
import { formatDecimal } from "../../utils/numberFormat";
import { getVendorCurrency } from "../../utils/currency";

/**
 * Shared export control reused by every Inventory report and the item
 * history timeline - Excel is generated entirely client-side (this is the
 * xlsx package's first real *write* use in this app; it was previously
 * only used to read uploaded files), PDF is rendered server-side via the
 * backend's Puppeteer-based export endpoint.
 *
 * Props:
 *   rows          {array}    - the already-fetched report rows
 *   columns       [{key,label,format?}] - which fields to include, in order;
 *                              format: "date" formats the raw value with
 *                              formatReportDate, format: "number" with
 *                              formatDecimal, format: "currency" with the
 *                              vendor's currency code + formatDecimal, so
 *                              exports match whatever the on-screen table's
 *                              own render shows for that column, instead of
 *                              the raw API value.
 *   filenameBase  {string}   - e.g. "stock_movement_report"
 *   onExportPdf   {function} - async (htmlContent) => void, wired to the
 *                              screen's own inventoryService.exportReportPdf /
 *                              exportItemHistoryPdf call
 */
function ExportMenu({ rows, columns, filenameBase = "inventory_export", onExportPdf }) {
  const [exporting, setExporting] = useState(false);

  const cellValue = (row, col) => {
    if (col.format === "date") return formatReportDate(row[col.key]);
    if (col.format === "number") return formatDecimal(row[col.key]);
    if (col.format === "currency") return `${getVendorCurrency()} ${formatDecimal(row[col.key])}`;
    return row[col.key];
  };

  const handleExcel = () => {
    if (!rows || rows.length === 0) {
      toast.info("Nothing to export yet.");
      return;
    }
    const mapped = rows.map((row) =>
      columns.reduce((acc, col) => {
        acc[col.label] = cellValue(row, col);
        return acc;
      }, {})
    );
    const worksheet = XLSX.utils.json_to_sheet(mapped);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
    XLSX.writeFile(workbook, `${filenameBase}.xlsx`);
  };

  const buildHtmlTable = () => {
    const head = `<tr>${columns.map((c) => `<th>${c.label}</th>`).join("")}</tr>`;
    const body = rows
      .map((row) => `<tr>${columns.map((c) => `<td>${cellValue(row, c) ?? ""}</td>`).join("")}</tr>`)
      .join("");
    return `<table><thead>${head}</thead><tbody>${body}</tbody></table>`;
  };

  const handlePdf = async () => {
    if (!rows || rows.length === 0) {
      toast.info("Nothing to export yet.");
      return;
    }
    setExporting(true);
    try {
      await onExportPdf(buildHtmlTable());
    } catch (err) {
      toast.error(err.message || "PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dropdown align="end">
      <style>{`
        .inv-export-toggle.dropdown-toggle {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border-radius: 8px;
          border: 1.5px solid #E8EAF0;
          background: #fff;
          color: #475569;
          font-size: 13px;
          font-weight: 500;
          transition: all 0.15s;
          font-family: inherit;
        }
        .inv-export-toggle.dropdown-toggle:hover:not(:disabled),
        .inv-export-toggle.dropdown-toggle:focus:not(:disabled) {
          background: #F8F9FC !important;
          border-color: #CBD5E1 !important;
          color: #0F172A !important;
        }
        .inv-export-toggle.dropdown-toggle:disabled {
          opacity: 0.6;
        }
        .inv-export-menu.dropdown-menu {
          border-radius: 14px;
          border: 1.5px solid #E8EAF0;
          box-shadow: 0 16px 40px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06);
          min-width: 220px;
          padding: 6px;
          overflow: hidden;
        }
        .inv-export-item.dropdown-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 11px 14px;
          border-radius: 9px;
          background: transparent;
          font-family: inherit;
        }
        .inv-export-item.dropdown-item:hover,
        .inv-export-item.dropdown-item:focus,
        .inv-export-item.dropdown-item:focus-visible {
          background-color: #F8F9FC !important;
          color: #0F172A !important;
        }
        .inv-export-item-icon {
          font-size: 20px;
          flex-shrink: 0;
        }
        .inv-export-item-label {
          font-size: 13px;
          font-weight: 600;
          color: #0F172A;
        }
        .inv-export-item-sub {
          font-size: 11px;
          color: #94A3B8;
          margin-top: 2px;
        }
        .inv-export-divider {
          height: 1px;
          background: #F1F5F9;
          margin: 4px 8px;
        }
      `}</style>
      <Dropdown.Toggle
        variant="light"
        disabled={exporting}
        className="inv-export-toggle"
      >
        {exporting ? "Exporting…" : "Export"}
      </Dropdown.Toggle>
      <Dropdown.Menu className="inv-export-menu">
        <Dropdown.Item className="inv-export-item" onClick={handleExcel}>
          <span className="inv-export-item-icon">📄</span>
          <div>
            <div className="inv-export-item-label">Export as Excel</div>
            <div className="inv-export-item-sub">Download as .xlsx spreadsheet</div>
          </div>
        </Dropdown.Item>
        <div className="inv-export-divider" />
        <Dropdown.Item className="inv-export-item" onClick={handlePdf}>
          <span className="inv-export-item-icon">🖨️</span>
          <div>
            <div className="inv-export-item-label">Export as PDF</div>
            <div className="inv-export-item-sub">Formatted report document</div>
          </div>
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
}

export default ExportMenu;
